import { useCallback, useRef, useState } from 'react';
import { deleteJobPoolItem, JOB_POOL_DELETE_ERROR_MESSAGES } from '../../services/jobPoolApi.js';

// 行が無くなったときにフォーカスを移す先の印。一覧の先頭 (見出しがあれば見出し、0 件で見出しが無ければ
// 求人プール件数〈DP-M-008〉) へ移す (§6.2 手順 3。PM の判断 #883 comment 6008263645 の ④)
export const FOCUS_LIST_TOP = 'list-top';

/**
 * 求人プールからの削除 (BTN-M-014) の状態と操作 (画面レイアウト_SCR-M-001.md §6.2「求人プールからの削除（BTN-M-014）」、
 * Issue #883)。確認のモーダルの対象・削除中・失敗の文言・削除できたときの文言と、削除の後に
 * フォーカスを移す行を持つ。一覧・選択・推薦結果の画面からの取り除きは removeJobPoolItem (useAiMatching) が行う。
 *
 * 削除中かは押した時点の印 (ref) で判定する (描画の時点の状態では、再描画の前に 2 回押すと 2 通送られうる。#992)。
 *
 * @param {object[]} items 一覧 (DP-M-009) の求人。削除の後のフォーカスの移し先を決めるのに使う
 * @param {(jobPoolItemId: string) => void} removeJobPoolItem 削除できた求人を画面から取り除く
 */
function useJobPoolDelete(items, removeJobPoolItem) {
    // 確認のモーダルを開いている求人 ({ id, company_name, job_title })。閉じていれば null
    const [target, setTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // 削除できなかったときの文言 (モーダルの本文の下。role="alert")
    const [errorMessage, setErrorMessage] = useState(null);
    // 削除できたときの文言 (一覧の上の role="status")
    const [statusMessage, setStatusMessage] = useState(null);
    // 削除の後にフォーカスを移す先 (行の求人の ID か FOCUS_LIST_TOP)。移したら clearFocusRequest で消す
    const [focusRequest, setFocusRequest] = useState(null);
    const deletingRef = useRef(false);

    const openDelete = useCallback((item) => {
        if (deletingRef.current) return;
        setTarget({ id: item.id, company_name: item.company_name, job_title: item.job_title });
        setErrorMessage(null);
    }, []);

    // 「キャンセル」。削除中は閉じない (応答を受け取るまでボタンを押せなくする。§6.2 手順 2)
    const cancelDelete = useCallback(() => {
        if (deletingRef.current) return;
        setTarget(null);
        setErrorMessage(null);
    }, []);

    // 取り除いた行の次の行、最後の行なら前の行、行が無くなれば一覧の先頭 (§6.2 手順 3)
    const nextFocusAfter = useCallback(
        (removedId) => {
            const index = items.findIndex((item) => item.id === removedId);
            const rest = items.filter((item) => item.id !== removedId);
            if (rest.length === 0) return FOCUS_LIST_TOP;
            if (index < 0) return rest[0].id;
            return (rest[index] || rest[index - 1]).id;
        },
        [items]
    );

    const confirmDelete = useCallback(async () => {
        if (!target || deletingRef.current) return;
        deletingRef.current = true;
        setDeleting(true);
        setErrorMessage(null);
        try {
            let result;
            try {
                result = await deleteJobPoolItem(target.id);
            } catch (err) {
                // 失敗は deleteJobPoolItem が結果で返す。ここに来るのは想定外の例外だけ。
                // 削除が済んでいる場合があるため、通信の失敗と同じ文言にする (§6.3 No.3)
                console.error('useJobPoolDelete: 求人プールからの削除で例外が発生しました', err);
                result = { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.network };
            }

            if (result.ok || result.notFound) {
                // 404 (もう無い) も、消したい状態にはなっているため、削除できたときと同じく閉じて取り除く (§6.3 No.2)
                const focusTo = nextFocusAfter(target.id);
                removeJobPoolItem(target.id);
                setTarget(null);
                setStatusMessage(
                    result.ok
                        ? `「${target.company_name}／${target.job_title}」を求人プールから削除しました。`
                        : result.errorMessage
                );
                setFocusRequest(focusTo);
            } else {
                // モーダルは開いたまま、もう一度押せるように戻す。一覧・推薦結果は変えない (§6.2 手順 4)
                setErrorMessage(result.errorMessage);
            }
        } finally {
            deletingRef.current = false;
            setDeleting(false);
        }
    }, [target, nextFocusAfter, removeJobPoolItem]);

    const clearFocusRequest = useCallback(() => setFocusRequest(null), []);

    return {
        deleteTarget: target,
        jobPoolDeleting: deleting,
        jobPoolDeleteError: errorMessage,
        jobPoolDeleteStatus: statusMessage,
        deleteFocusRequest: focusRequest,
        openDelete,
        cancelDelete,
        confirmDelete,
        clearFocusRequest,
    };
}

export default useJobPoolDelete;
